import type { TeamMember } from "@bezoned/shared/types/api/team/team-member-list.types.js";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  teamMemberCreate,
  teamMemberDelete,
} from "../../../../../services/api.service.js";
import { recordErrorInSentry } from "../../../../../utils/init-sentry.js";
import type { ToastDispatcher } from "../types.js";

interface UseTeamMemberMutationsParams {
  msTeamId: string | null;
  dispatchSuccessToast: ToastDispatcher;
  dispatchFailureToast: ToastDispatcher;
  onDeleteSuccess: () => void;
}

export const useTeamMemberMutations = ({
  msTeamId,
  dispatchSuccessToast,
  dispatchFailureToast,
  onDeleteSuccess,
}: UseTeamMemberMutationsParams) => {
  const queryClient = useQueryClient();

  const addMutation = useMutation({
    mutationFn: (userIds: string[]) => {
      if (!msTeamId) {
        throw new Error("Office ID is required to add members");
      }

      return teamMemberCreate({ msTeamId, msUserIds: userIds });
    },
    onError: (error) => {
      recordErrorInSentry(error);
      dispatchFailureToast({
        message: "Failed to add office members. Please try again.",
      });
    },
    onSuccess: (data) => {
      if (msTeamId) {
        queryClient.invalidateQueries({
          queryKey: ["getTeamMembers", msTeamId],
        });
      }

      const { succeeded, failed } = data.results;

      if (succeeded.length > 0) {
        const message =
          succeeded.length === 1
            ? "Office member added."
            : `${succeeded.length} new office members added.`;
        dispatchSuccessToast({ message });
      }

      if (failed.length > 0) {
        const message =
          failed.length === 1
            ? "Failed to add 1 office member."
            : `Failed to add ${failed.length} office members.`;
        dispatchFailureToast({ message });
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ msMemberId }: { msMemberId: string }) => {
      if (!msTeamId) {
        throw new Error("Office ID is required to remove members");
      }

      return teamMemberDelete({
        msMemberId,
        msTeamId,
      });
    },
    onError: (error, _variables, _context) => {
      recordErrorInSentry(error);
      dispatchFailureToast({
        message: "Failed to remove office member. Please try again.",
      });
    },
    onMutate: async () => {
      if (msTeamId) {
        await queryClient.cancelQueries({
          queryKey: ["getTeamMembers", msTeamId],
        });
      }
    },
    onSuccess: (_data, _variables, _context) => {
      if (msTeamId) {
        queryClient.invalidateQueries({
          queryKey: ["getTeamMembers", msTeamId],
        });
      }
      dispatchSuccessToast({ message: "Office member removed." });
      onDeleteSuccess();
    },
  });

  const addMembers = (userIds: string[]) => {
    addMutation.mutate(userIds);
  };

  const deleteMember = (member: TeamMember) => {
    deleteMutation.mutate({
      msMemberId: member.id,
    });
  };

  return {
    addMembers,
    deleteMember,
    isAdding: addMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
